import { useId, useMemo, useState, type FormEvent } from "react";
import { useEditAsset, useEditIdentity, useEditLocation, useGeocode, type SiteDetail } from "../../api/sites";
import { SiteMap, type MapSite } from "../dashboard/SiteMap";

/** The site's editable blocks: identity, asset and position. The office edits
 * them; a crew reads them. Every save is one request and leaves one event in
 * the history, so nothing here saves as you type. */

/** The region choice for clusters that have no region yet. */
const NO_REGION = "none";

export function IdentityTab({ site }: { site: SiteDetail }) {
  const edit = useEditIdentity(site.id);
  const [form, setForm] = useState({ name: site.name, ...site.identity });
  const areas = site.areas;
  const [regionId, setRegionId] = useState(site.region ? String(site.region.id) : NO_REGION);
  const [clusterId, setClusterId] = useState(site.cluster ? String(site.cluster.id) : "");
  const clustersIn = (region: string) =>
    (areas?.clusters ?? []).filter((c) => (region === NO_REGION ? c.region_id === null : String(c.region_id) === region));
  const unplaced = (areas?.clusters ?? []).some((c) => c.region_id === null);
  const placeChosen = areas === null || clusterId !== "";

  function chooseRegion(next: string) {
    setRegionId(next);
    // A cluster from the region left behind is not a choice in the new one.
    if (!clustersIn(next).some((c) => String(c.id) === clusterId)) {
      setClusterId("");
    }
  }
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    // Only a cluster somebody chose here is sent. Resending the one the form
    // opened with would undo a move made since -- by another office user, a
    // Red List upload or SAP's work order export -- on a save that only fixed
    // a typo.
    const opened = site.cluster ? String(site.cluster.id) : "";
    const moved = areas !== null && clusterId !== "" && clusterId !== opened;
    edit.mutate(moved ? { ...form, cluster_id: Number(clusterId) } : form);
  }
  // The works order is the City's notification number, as the Sites register
  // and every screen call it; the RS number is the QCP's works project number.
  const fields: [keyof typeof form, string][] = [
    ["name", "Name"],
    ["facility", "Facility"],
    ["address", "Address"],
    ["notif_no", "Works order (notification no.)"],
    ["rs_no", "RS no."],
    ["func_loc", "Functional location"],
    ["equip_no", "Equipment no."],
    ["station_type", "Station type"],
  ];
  return (
    <form className="site-form" onSubmit={onSubmit}>
      {areas !== null ? (
        <AreaFields
          regions={areas.regions}
          clusters={clustersIn(regionId)}
          unplaced={unplaced || site.region === null}
          regionId={regionId}
          clusterId={clusterId}
          onRegion={chooseRegion}
          onCluster={setClusterId}
        />
      ) : (
        <p className="site-form-wide site-area-line">
          {[site.region?.name ?? "No region", site.cluster?.name ?? "No cluster"].join(" · ")}
        </p>
      )}
      {fields.map(([key, label]) => (
        <label key={key} className={key === "address" ? "field site-form-wide" : "field"}>
          {label}
          <input
            value={(form[key] as string | null) ?? ""}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            disabled={!site.editable}
          />
        </label>
      ))}
      <SaveRow editable={site.editable} pending={edit.isPending} saved={edit.isSuccess} label="Save identity" blocked={!placeChosen} />
      {!placeChosen ? <p className="muted site-form-wide">Choose a cluster in the region before saving.</p> : null}
      {edit.isError ? <p className="notice bad site-form-wide">{edit.error.message}</p> : null}
    </form>
  );
}

/** Region, then the clusters in it. The region is the cluster's, so it is the
 * cluster that is saved; the region only narrows the choice. */
function AreaFields({
  regions,
  clusters,
  unplaced,
  regionId,
  clusterId,
  onRegion,
  onCluster,
}: {
  regions: readonly { id: number; name: string }[];
  clusters: readonly { id: number; name: string }[];
  unplaced: boolean;
  regionId: string;
  clusterId: string;
  onRegion: (next: string) => void;
  onCluster: (next: string) => void;
}) {
  const regionField = useId();
  const clusterField = useId();
  return (
    <>
      <div className="field">
        <label htmlFor={regionField}>Region</label>
        <select id={regionField} value={regionId} onChange={(e) => onRegion(e.target.value)}>
          {regions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
          {unplaced ? <option value={NO_REGION}>No region</option> : null}
        </select>
      </div>
      <div className="field">
        <label htmlFor={clusterField}>Cluster</label>
        <select id={clusterField} value={clusterId} onChange={(e) => onCluster(e.target.value)} required>
          <option value="">Choose a cluster…</option>
          {clusters.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}

export function AssetTab({ site }: { site: SiteDetail }) {
  const edit = useEditAsset(site.id);
  const [form, setForm] = useState({
    valve_make: site.asset.valve_make ?? "",
    valve_desc: site.asset.valve_desc ?? "",
    dn_scope: site.asset.dn_scope === null ? "" : String(site.asset.dn_scope),
    dn_boq: site.asset.dn_boq === null ? "" : String(site.asset.dn_boq),
    scope_type: site.asset.scope_type,
  });
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    edit.mutate({
      valve_make: form.valve_make || null,
      valve_desc: form.valve_desc || null,
      dn_scope: form.dn_scope === "" ? null : Number(form.dn_scope),
      dn_boq: form.dn_boq === "" ? null : Number(form.dn_boq),
      scope_type: form.scope_type,
    });
  }
  return (
    <form className="site-form" onSubmit={onSubmit}>
      <label className="field">
        Valve make
        <input value={form.valve_make} onChange={(e) => setForm({ ...form, valve_make: e.target.value })} disabled={!site.editable} />
      </label>
      <label className="field">
        Scope
        <select value={form.scope_type} onChange={(e) => setForm({ ...form, scope_type: e.target.value })} disabled={!site.editable}>
          <option value="maintenance">Maintenance</option>
          <option value="replacement">Replacement</option>
        </select>
      </label>
      <label className="field site-form-wide">
        Valve description
        <input value={form.valve_desc} onChange={(e) => setForm({ ...form, valve_desc: e.target.value })} disabled={!site.editable} />
      </label>
      <label className="field">
        DN (scope)
        <input
          value={form.dn_scope}
          inputMode="numeric"
          onChange={(e) => setForm({ ...form, dn_scope: e.target.value })}
          disabled={!site.editable}
        />
      </label>
      <label className="field">
        DN (BOQ)
        <input
          value={form.dn_boq}
          inputMode="numeric"
          onChange={(e) => setForm({ ...form, dn_boq: e.target.value })}
          disabled={!site.editable}
        />
      </label>
      <SaveRow editable={site.editable} pending={edit.isPending} saved={edit.isSuccess} label="Save asset" />
      {edit.isError ? <p className="notice bad site-form-wide">{edit.error.message}</p> : null}
    </form>
  );
}

function SaveRow({
  editable,
  pending,
  saved,
  label,
  blocked = false,
}: {
  editable: boolean;
  pending: boolean;
  saved: boolean;
  label: string;
  blocked?: boolean;
}) {
  if (!editable) {
    return <p className="muted site-form-wide">Only the office can change these.</p>;
  }
  return (
    <div className="site-form-wide site-save-row">
      <button type="submit" className="btn btn-primary" disabled={pending || blocked}>
        {pending ? "Saving…" : label}
      </button>
      {saved && !pending ? (
        <span className="muted" role="status">
          Saved. The change is in the site history.
        </span>
      ) : null}
    </div>
  );
}

// The one pin, in the red the register marks what needs attention with.
const PIN_COLOUR = "#b23b3b";

export function LocationTab({ site }: { site: SiteDetail }) {
  const edit = useEditLocation(site.id);
  const geocode = useGeocode(site.id);
  const [lat, setLat] = useState(site.location.lat === null ? "" : String(site.location.lat));
  const [lon, setLon] = useState(site.location.lon === null ? "" : String(site.location.lon));
  const [address, setAddress] = useState(site.identity.address ?? "");
  const has = site.location.lat !== null && site.location.lon !== null;
  // Memoised on the position: the map redraws when the site moves, not on
  // every keystroke in the fields below.
  const mapped = useMemo<MapSite[]>(
    () =>
      site.location.lat === null || site.location.lon === null
        ? []
        : [{ id: site.id, name: site.name, lat: site.location.lat, lon: site.location.lon, status: site.status, label: "" }],
    [site.id, site.name, site.status, site.location.lat, site.location.lon],
  );
  const pin = useMemo(() => ({ [site.status]: PIN_COLOUR }), [site.status]);
  return (
    <>
      <dl className="site-facts">
        <div>
          <dt>Position</dt>
          <dd>
            {has ? (
              <>
                {site.location.lat?.toFixed(6)}, {site.location.lon?.toFixed(6)}{" "}
                <a
                  className="go-link"
                  href={`https://www.google.com/maps?q=${site.location.lat},${site.location.lon}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  map
                </a>
              </>
            ) : (
              <em>Not located</em>
            )}
          </dd>
        </div>
        <div>
          <dt>Source</dt>
          <dd>
            {site.location.source_label ?? "—"}
            {site.location.confidence ? ` (${site.location.confidence})` : ""}
          </dd>
        </div>
        {site.location.lo19 ? (
          <div>
            <dt>Lo19</dt>
            <dd>
              y {site.location.lo19.y.toFixed(2)}, x {site.location.lo19.x.toFixed(2)}
            </dd>
          </div>
        ) : null}
      </dl>
      {mapped.length > 0 ? <SiteMap sites={mapped} colours={pin} compact /> : null}
      {site.editable ? (
        <section className="site-move">
          <form
            className="site-inline"
            onSubmit={(e) => {
              e.preventDefault();
              edit.mutate({ lat: lat === "" ? null : Number(lat), lon: lon === "" ? null : Number(lon) });
            }}
          >
            <label className="field">
              Latitude
              <input value={lat} onChange={(e) => setLat(e.target.value)} className="site-num-input" inputMode="decimal" />
            </label>
            <label className="field">
              Longitude
              <input value={lon} onChange={(e) => setLon(e.target.value)} className="site-num-input" inputMode="decimal" />
            </label>
            <button type="submit" className="btn btn-secondary" disabled={edit.isPending}>
              {lat === "" && lon === "" ? "Clear position" : "Set by hand"}
            </button>
          </form>
          <form
            className="site-inline"
            onSubmit={(e) => {
              e.preventDefault();
              geocode.mutate(address);
            }}
          >
            <label className="field site-grow">
              Or find by address
              <input value={address} onChange={(e) => setAddress(e.target.value)} />
            </label>
            <button type="submit" className="btn btn-secondary" disabled={geocode.isPending || address.trim() === ""}>
              {geocode.isPending ? "Looking up…" : "Look up address"}
            </button>
          </form>
          <p className="muted site-small">A hand-set point is recorded as manual/high. A looked-up one is a geocoded guess and says so.</p>
        </section>
      ) : null}
      {edit.isError ? <p className="notice bad">{edit.error.message}</p> : null}
      {geocode.isError ? <p className="notice bad">{geocode.error.message}</p> : null}
    </>
  );
}
